import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClipboardIcon,
  CheckIcon,
  ArrowPathIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { useDiagnosticsStore } from '../../stores/diagnosticsStore';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import type { FormPortal } from '@affogato/shared';

interface PortalSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  portal?: FormPortal;
}

export function PortalSettingsModal({
  isOpen,
  onClose,
  formId,
  portal,
}: PortalSettingsModalProps) {
  const { t } = useTranslation('diagnostics');
  const { createOrUpdatePortal, regenerateToken, isLoading } = useDiagnosticsStore();

  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [maxResponses, setMaxResponses] = useState('');
  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isCreatingPortal, setIsCreatingPortal] = useState(false);
  const [currentToken, setCurrentToken] = useState(portal?.token || '');
  const hasAutoCreated = useRef(false);

  useEffect(() => {
    if (portal) {
      setIsActive(portal.isActive);
      setExpiresAt(portal.expiresAt || '');
      setMaxResponses(portal.maxResponses?.toString() || '');
      setCurrentToken(portal.token);
    }
  }, [portal]);

  // Auto-create portal when modal opens if no portal exists
  useEffect(() => {
    if (isOpen && !portal && !currentToken && !hasAutoCreated.current) {
      hasAutoCreated.current = true;
      setIsCreatingPortal(true);
      createOrUpdatePortal(formId, { isActive: true })
        .then((result) => {
          setCurrentToken(result.token);
          setIsActive(result.isActive);
        })
        .catch(console.error)
        .finally(() => setIsCreatingPortal(false));
    }
    // Reset ref when modal closes
    if (!isOpen) {
      hasAutoCreated.current = false;
    }
  }, [isOpen, portal, currentToken, formId, createOrUpdatePortal]);

  const portalUrl = currentToken
    ? `${window.location.origin}/f/${currentToken}`
    : '';

  const handleCopy = async () => {
    if (!portalUrl) return;
    await navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await createOrUpdatePortal(formId, {
        password: password || undefined,
        isActive,
        expiresAt: expiresAt || undefined,
        maxResponses: maxResponses ? parseInt(maxResponses, 10) : undefined,
      });
      setCurrentToken(result.token);
      setPassword(''); // Clear password after save
      onClose();
    } catch (error) {
      console.error('Error saving portal settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerateToken = async () => {
    if (!confirm(t('portal.regenerateConfirm'))) return;

    setIsRegenerating(true);
    try {
      const newToken = await regenerateToken(formId);
      setCurrentToken(newToken);
    } catch (error) {
      console.error('Error regenerating token:', error);
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('portal.title')}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-slate-400">
          {t('portal.description')}
        </p>

        {/* Share Link */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            {t('portal.shareLink')}
          </label>
          {isCreatingPortal ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg">
              <Spinner size="sm" />
              <span className="text-sm text-gray-500 dark:text-slate-400">Generando enlace...</span>
            </div>
          ) : currentToken ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={portalUrl}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-300"
              />
              <Button variant="secondary" onClick={handleCopy}>
                {copied ? (
                  <CheckIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <ClipboardIcon className="w-4 h-4" />
                )}
                {copied ? t('portal.copied') : t('portal.copyLink')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <LinkIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              <span className="text-sm text-yellow-700 dark:text-yellow-300">
                Guarda la configuración para generar el enlace
              </span>
            </div>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            {t('portal.password')}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('portal.passwordPlaceholder')}
            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-slate-500"
          />
        </div>

        {/* Active Toggle */}
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm font-medium text-gray-700 dark:text-slate-300">
            {t('portal.isActive')}
          </span>
          <button
            type="button"
            onClick={() => setIsActive(!isActive)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isActive ? 'bg-primary-600' : 'bg-gray-200 dark:bg-slate-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </label>

        {/* Expiration Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            {t('portal.expiresAt')}
          </label>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Max Responses */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
            {t('portal.maxResponses')}
          </label>
          <input
            type="number"
            min="1"
            value={maxResponses}
            onChange={(e) => setMaxResponses(e.target.value)}
            placeholder="0"
            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-slate-500"
          />
        </div>

        {/* Regenerate Token */}
        {currentToken && (
          <button
            onClick={handleRegenerateToken}
            disabled={isRegenerating}
            className="text-sm text-gray-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1 transition-colors"
          >
            {isRegenerating ? (
              <Spinner size="sm" />
            ) : (
              <ArrowPathIcon className="w-4 h-4" />
            )}
            {t('portal.regenerateToken')}
          </button>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
          <Button variant="ghost" onClick={onClose}>
            {t('portal.close')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? <Spinner size="sm" /> : t('portal.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
